import { FormEvent, useEffect, useState } from "react";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import {
  useAddDealToLink,
  useDealLink,
  useRemoveDealFromLink,
  useUpdateDealLink,
} from "src/dm/hooks/useDealLink";
import {
  DealLinkDto,
  DealLinkWriteRequest,
} from "src/dm/interfaces/DealLink";

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

// Convert ISO timestamps from the API into the yyyy-MM-dd value an
// <input type="date"> wants. Tolerates already-truncated strings.
const toDateInput = (iso: string | null | undefined): string =>
  iso ? iso.substring(0, 10) : "";

const toIsoOrNull = (s: string): string | null =>
  s.trim() === "" ? null : s;

const toNumberOrNull = (s: string): number | null => {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const dtoToFormState = (link: DealLinkDto): EditState => ({
  description: link.description ?? "",
  adBlockId: link.adBlockId?.toString() ?? "",
  adPlanCode: link.adPlanCode ?? "",
  adLocationId: link.adLocationId?.toString() ?? "",
  adDistributionId: link.adDistributionId?.toString() ?? "",
  bestHistoryAdDate: toDateInput(link.bestHistoryAdDate),
  signageEndDate: toDateInput(link.signageEndDate),
  signageNotes: link.signageNotes ?? "",
  projectedUnits: link.projectedUnits?.toString() ?? "",
  projectedUnitsUom: link.projectedUnitsUom ?? "",
  priceFormatId: link.priceFormatId?.toString() ?? "",
  displayPlanId: link.displayPlanId?.toString() ?? "",
  adFeatureId: link.adFeatureId?.toString() ?? "",
  adFeatureSequence: link.adFeatureSequence?.toString() ?? "",
  adStrength: link.adStrength ?? "",
  countryOfOrigin: link.countryOfOrigin ?? "",
  otherCharges: link.otherCharges?.toString() ?? "",
  dealCost: link.dealCost?.toString() ?? "",
  couponComment: link.couponComment ?? "",
  ampStoreComment: link.ampStoreComment ?? "",
  ampMerchandisingComment: link.ampMerchandisingComment ?? "",
  ampAdvertisingComment: link.ampAdvertisingComment ?? "",
  isDemo: link.isDemo,
  caseDiscount: link.caseDiscount,
  wineSaveUpTo: link.wineSaveUpTo,
  suppressSavePriceFormat: link.suppressSavePriceFormat,
});

interface EditState {
  description: string;
  adBlockId: string;
  adPlanCode: string;
  adLocationId: string;
  adDistributionId: string;
  bestHistoryAdDate: string;
  signageEndDate: string;
  signageNotes: string;
  projectedUnits: string;
  projectedUnitsUom: string;
  priceFormatId: string;
  displayPlanId: string;
  adFeatureId: string;
  adFeatureSequence: string;
  adStrength: string;
  countryOfOrigin: string;
  otherCharges: string;
  dealCost: string;
  couponComment: string;
  ampStoreComment: string;
  ampMerchandisingComment: string;
  ampAdvertisingComment: string;
  isDemo: boolean;
  caseDiscount: boolean;
  wineSaveUpTo: boolean;
  suppressSavePriceFormat: boolean;
}

const formToRequest = (state: EditState): DealLinkWriteRequest => ({
  description: state.description,
  adBlockId: toNumberOrNull(state.adBlockId),
  adPlanCode: state.adPlanCode || null,
  adLocationId: toNumberOrNull(state.adLocationId),
  adDistributionId: toNumberOrNull(state.adDistributionId),
  bestHistoryAdDate: toIsoOrNull(state.bestHistoryAdDate),
  signageEndDate: toIsoOrNull(state.signageEndDate),
  signageNotes: state.signageNotes || null,
  projectedUnits: toNumberOrNull(state.projectedUnits),
  projectedUnitsUom: state.projectedUnitsUom || null,
  priceFormatId: toNumberOrNull(state.priceFormatId),
  displayPlanId: toNumberOrNull(state.displayPlanId),
  adFeatureId: toNumberOrNull(state.adFeatureId),
  adFeatureSequence: toNumberOrNull(state.adFeatureSequence),
  adStrength: state.adStrength || null,
  countryOfOrigin: state.countryOfOrigin || null,
  otherCharges: toNumberOrNull(state.otherCharges),
  dealCost: toNumberOrNull(state.dealCost),
  couponComment: state.couponComment || null,
  ampStoreComment: state.ampStoreComment || null,
  ampMerchandisingComment: state.ampMerchandisingComment || null,
  ampAdvertisingComment: state.ampAdvertisingComment || null,
  isDemo: state.isDemo,
  caseDiscount: state.caseDiscount,
  wineSaveUpTo: state.wineSaveUpTo,
  suppressSavePriceFormat: state.suppressSavePriceFormat,
});

// Shared inline styles for the edit grid. Kept colocated rather than spun
// out to a .css.ts file — the page-level styles in DealLayout already give
// us card chrome, and the rest is just a 2-column form layout.
const fieldGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "0.75rem 1rem",
};
const labelStyle = {
  display: "block",
  fontSize: "0.7rem",
  color: "#64748b",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  marginBottom: "0.15rem",
};
const inputStyle = {
  width: "100%",
  padding: "0.35rem 0.5rem",
  border: "1px solid #cbd5e1",
  borderRadius: "4px",
  fontSize: "0.9rem",
  boxSizing: "border-box" as const,
};
const sectionHeading = {
  margin: "1.25rem 0 0.5rem",
  fontSize: "0.85rem",
  color: "#0a3d62",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
};

const DealLinkPage = () => {
  const deal = useDealContext();
  const { data, isLoading, isError, error } = useDealLink(deal.dealId);

  const linkId = data?.link.linkId;
  const update = useUpdateDealLink(linkId ?? 0);
  const addMember = useAddDealToLink(linkId ?? 0);
  const removeMember = useRemoveDealFromLink(linkId ?? 0);

  const [form, setForm] = useState<EditState | null>(null);
  const [addDealIdInput, setAddDealIdInput] = useState("");
  const [bannerError, setBannerError] = useState<string | null>(null);

  // Sync form state when the server payload arrives or after a successful
  // save. Without this the inputs would stay stuck on stale values.
  useEffect(() => {
    if (data?.link) setForm(dtoToFormState(data.link));
  }, [data?.link]);

  if (isLoading) return <p>Loading link…</p>;
  if (isError) {
    return (
      <p style={{ color: "#b91c1c" }}>
        {error instanceof Error ? error.message : "Failed to load link"}
      </p>
    );
  }

  if (!data) {
    // useDealLink returns null when the deal has no link yet (HTTP 404
    // surfaces as data=null). The legacy app sends the user to ViewLink with
    // dealIds=... to build a new link from the Dashboard; that flow is
    // deferred per the scope notes. For now we display an empty state.
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Link</h2>
        <p style={{ color: "#94a3b8" }}>
          This deal is not part of a link. Use the Dashboard's "Link Deals"
          action to create a new link or attach this deal to an existing one.
        </p>
      </div>
    );
  }

  const { link, deals } = data;
  if (!form) return <p>Loading link…</p>;

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    await update.mutateAsync(formToRequest(form));
  };

  const handleAdd = async () => {
    setBannerError(null);
    const id = Number(addDealIdInput);
    if (!Number.isFinite(id) || id <= 0) return;
    try {
      await addMember.mutateAsync(id);
      setAddDealIdInput("");
    } catch (err: unknown) {
      // Surface the banner-mismatch 409 inline rather than throwing an
      // unhandled rejection past the user.
      const r = (err as { response?: { status?: number; data?: { error?: string } } }).response;
      if (r?.status === 409) {
        setBannerError(r.data?.error ?? "Deal banner does not match link.");
      } else if (r?.status === 404) {
        setBannerError("Deal not found.");
      } else {
        setBannerError("Failed to add deal to link.");
      }
    }
  };

  const handleRemove = async (dealIdToRemove: number) => {
    if (
      !confirm(
        `Remove deal ${dealIdToRemove} from link ${link.linkId}? If this is the last deal, the link will also be deleted.`
      )
    ) {
      return;
    }
    await removeMember.mutateAsync(dealIdToRemove);
  };

  const set = <K extends keyof EditState>(key: K, value: EditState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  // Custom delete-cell renderer for the linked-deals grid. The current deal
  // can also be removed, but we keep the button enabled so the user can
  // detach it from this view and be redirected back to its own page.
  const DeleteCell = (props: GridCellProps) => {
    const row = props.dataItem as { dealId: number; isLocked: boolean };
    const disabled = row.isLocked || removeMember.isPending;
    return (
      <td>
        <Button
          type="button"
          size="small"
          themeColor="error"
          fillMode="flat"
          onClick={() => handleRemove(row.dealId)}
          disabled={disabled}
        >
          Remove
        </Button>
      </td>
    );
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>
        Link #{link.linkId}
        {link.adBlockId && (
          <span style={{ color: "#64748b", fontSize: "0.85rem", marginLeft: "0.75rem" }}>
            Ad Block {link.adBlockId}
          </span>
        )}
      </h2>

      <h3 style={sectionHeading}>Deals in this link ({deals.length})</h3>
      <Grid data={deals}>
        <GridColumn field="dealId" title="Deal #" width="100px" />
        <GridColumn field="description" title="Description" />
        <GridColumn field="bannerCode" title="Banner" width="100px" />
        <GridColumn field="status" title="Status" width="120px" />
        <GridColumn
          field="adStartDate"
          title="Ad Start"
          width="120px"
          cells={{
            data: (p: GridCellProps) => (
              <td>{fmtDate((p.dataItem as { adStartDate: string | null }).adStartDate)}</td>
            ),
          }}
        />
        <GridColumn
          field="adEndDate"
          title="Ad End"
          width="120px"
          cells={{
            data: (p: GridCellProps) => (
              <td>{fmtDate((p.dataItem as { adEndDate: string | null }).adEndDate)}</td>
            ),
          }}
        />
        <GridColumn field="ownerUserId" title="Owner" width="120px" />
        <GridColumn title="" width="120px" cells={{ data: DeleteCell }} />
      </Grid>

      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <input
          type="number"
          placeholder="Deal # to add"
          value={addDealIdInput}
          onChange={(e) => setAddDealIdInput(e.target.value)}
          style={{ ...inputStyle, width: "180px" }}
        />
        <Button
          type="button"
          onClick={handleAdd}
          disabled={!addDealIdInput || addMember.isPending}
        >
          {addMember.isPending ? "Adding…" : "Add deal to link"}
        </Button>
        {bannerError && (
          <span style={{ color: "#b91c1c", fontSize: "0.85rem" }}>{bannerError}</span>
        )}
      </div>

      <form onSubmit={handleSave}>
        <h3 style={sectionHeading}>Link details</h3>
        <div style={fieldGrid}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Description</label>
            <input
              type="text"
              maxLength={60}
              required
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Ad Block ID</label>
            <input
              type="number"
              value={form.adBlockId}
              onChange={(e) => set("adBlockId", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Ad Plan</label>
            <select
              value={form.adPlanCode}
              onChange={(e) => set("adPlanCode", e.target.value)}
              style={inputStyle}
            >
              <option value="">—</option>
              <option value="P">Print All</option>
              <option value="N">Exclude All</option>
              <option value="R">Exclude Retails</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Ad Location ID</label>
            <input
              type="number"
              value={form.adLocationId}
              onChange={(e) => set("adLocationId", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Distribution ID</label>
            <input
              type="number"
              value={form.adDistributionId}
              onChange={(e) => set("adDistributionId", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Best History Ad</label>
            <input
              type="date"
              value={form.bestHistoryAdDate}
              onChange={(e) => set("bestHistoryAdDate", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Projected Units</label>
            <input
              type="number"
              value={form.projectedUnits}
              onChange={(e) => set("projectedUnits", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Projected UOM</label>
            <input
              type="text"
              maxLength={2}
              value={form.projectedUnitsUom}
              onChange={(e) => set("projectedUnitsUom", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Price Format ID</label>
            <input
              type="number"
              value={form.priceFormatId}
              onChange={(e) => set("priceFormatId", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Display Plan ID</label>
            <input
              type="number"
              value={form.displayPlanId}
              onChange={(e) => set("displayPlanId", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Ad Feature ID</label>
            <input
              type="number"
              value={form.adFeatureId}
              onChange={(e) => set("adFeatureId", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Ad Feature Sequence</label>
            <input
              type="number"
              value={form.adFeatureSequence}
              onChange={(e) => set("adFeatureSequence", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Ad Strength</label>
            <input
              type="text"
              maxLength={1}
              value={form.adStrength}
              onChange={(e) => set("adStrength", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Country of Origin</label>
            <input
              type="text"
              maxLength={20}
              value={form.countryOfOrigin}
              onChange={(e) => set("countryOfOrigin", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Other Charges</label>
            <input
              type="number"
              step="0.01"
              value={form.otherCharges}
              onChange={(e) => set("otherCharges", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Deal Cost</label>
            <input
              type="number"
              step="0.0001"
              value={form.dealCost}
              onChange={(e) => set("dealCost", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Signage End</label>
            <input
              type="date"
              value={form.signageEndDate}
              onChange={(e) => set("signageEndDate", e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <h3 style={sectionHeading}>Comments</h3>
        <div style={fieldGrid}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Signage Notes</label>
            <textarea
              maxLength={280}
              rows={2}
              value={form.signageNotes}
              onChange={(e) => set("signageNotes", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Coupon Comment</label>
            <textarea
              maxLength={200}
              rows={2}
              value={form.couponComment}
              onChange={(e) => set("couponComment", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>AMP Store Comment</label>
            <textarea
              maxLength={400}
              rows={2}
              value={form.ampStoreComment}
              onChange={(e) => set("ampStoreComment", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>AMP Merchandising Comment</label>
            <textarea
              maxLength={400}
              rows={2}
              value={form.ampMerchandisingComment}
              onChange={(e) => set("ampMerchandisingComment", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>AMP Advertising Comment</label>
            <textarea
              maxLength={200}
              rows={2}
              value={form.ampAdvertisingComment}
              onChange={(e) => set("ampAdvertisingComment", e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <h3 style={sectionHeading}>Flags</h3>
        <div style={fieldGrid}>
          <label>
            <input
              type="checkbox"
              checked={form.isDemo}
              onChange={(e) => set("isDemo", e.target.checked)}
            />{" "}
            Demo
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.caseDiscount}
              onChange={(e) => set("caseDiscount", e.target.checked)}
            />{" "}
            Case Discount
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.wineSaveUpTo}
              onChange={(e) => set("wineSaveUpTo", e.target.checked)}
            />{" "}
            Wine Save Up To
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.suppressSavePriceFormat}
              onChange={(e) => set("suppressSavePriceFormat", e.target.checked)}
            />{" "}
            No Save Price Format
          </label>
        </div>

        <div style={{ marginTop: "1.25rem", display: "flex", gap: "0.5rem" }}>
          <Button type="submit" themeColor="primary" disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          {update.isError && (
            <span style={{ color: "#b91c1c", alignSelf: "center" }}>
              {update.error instanceof Error
                ? update.error.message
                : "Save failed"}
            </span>
          )}
        </div>
      </form>
    </div>
  );
};

export default DealLinkPage;
